import {ChangeDetectorRef, Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild} from '@angular/core';
import {forkJoin, of} from 'rxjs';
import {catchError, map, mergeMap, take, tap} from 'rxjs/operators';
import {TestService} from '@webmate-web-core/shared/services/test.service';
import {ReportService} from '@webmate-web-core/shared/services/report.service';
import {FindingId, ProjectId} from '@webmate-web-core/shared/types/ids';
import {WmNotificationService} from '@webmate-web-core/shared/components/notification/wm-notification.service';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {InputText} from 'primeng/inputtext';
import {Popover} from 'primeng/popover';
import {MenuItem, PrimeTemplate} from 'primeng/api';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {Tooltip} from 'primeng/tooltip';
import {ActivatedRoute, Router} from '@angular/router';
import {Button} from 'primeng/button';
import {faCheck, faCopy, faShareAlt, faSyncAlt} from '@fortawesome/pro-solid-svg-icons';
import {collectRouteParams} from '@webmate-web-core/shared/utils/route-access';
import {copyToClipboard} from '@webmate-web-core/shared/utils/clipboard';
import {TieredMenu} from 'primeng/tieredmenu';
import {ModalToolContainerService} from '@webmate-web-core/shared/tools/modaltoolcontainer/modaltoolcontainer.service';
import {IssueReporterComponent, IssueReporterToolConfig} from '@webmate-web-core/shared/tools/issueexporter/issue-reporter.component';
import {IssueSourceType} from '@webmate-web-core/shared/types/integrations';
import {FeatureService} from '@webmate-web-core/shared/services/feature.service';

@Component({
  selector: 'wm-finding-share',
  standalone: true,
    imports: [
        FaIconComponent,
        InputText,
        Popover,
        PrimeTemplate,
        ReactiveFormsModule,
        Tooltip,
        Button,
        FormsModule,
        TieredMenu
    ],
  templateUrl: './finding-share.component.html',
  styleUrl: './finding-share.component.less'
})
export class FindingShareComponent implements OnInit, OnChanges {
    @ViewChild('sharePopover') sharePopover: Popover;
    @ViewChild('share') shareButton: Button;
    @ViewChild('menu') menu: TieredMenu;

    @Input() projectId?: ProjectId;
    @Input() optText?: string;
    @Input() findingIds: FindingId[];

    sharing: boolean = false;
    shareLink: string = "";
    shareLinkCopied: boolean;
    menuItems: MenuItem[] = [];

    constructor(private readonly testService: TestService,
                private readonly reportService: ReportService,
                private readonly notificationService: WmNotificationService,
                private readonly router: Router,
                private readonly route: ActivatedRoute,
                private readonly cdr: ChangeDetectorRef,
                private readonly toolService: ModalToolContainerService,
                private readonly featureService: FeatureService) {
        collectRouteParams(this.route).subscribe(params => {
            if (!this.projectId) {
                this.projectId = params['projectId'];
            }
        });
    }

    ngOnInit(): void {
        this.initMenu();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['findingIds'] && !changes['findingIds'].firstChange) {
            const prev: FindingId[] = changes['findingIds'].previousValue ?? [];
            const curr: FindingId[] = changes['findingIds'].currentValue ?? [];
            const changed = prev.length !== curr.length || prev.some((id, i) => id !== curr[i]);
            if (changed) {
                this.initMenu();
            }
        }
    }

    private initMenu(): void {
        const hasFindings = Array.isArray(this.findingIds) && this.findingIds.length > 0;
        if (!hasFindings) {
            this.menuItems = [];
            return;
        }

        const shareFindingEnabled$ = this.featureService.getFeature('webmate.auditlab').pipe(
            take(1),
            map(feature => !!feature?.isActive),
            catchError(() => of(false))
        );

        const hasIssueReporters$ = this.testService.getIssueReportersForCustomer().pipe(
            take(1),
            map(reporters => !!(reporters && reporters.length > 0)),
            catchError(() => of(false))
        );

        forkJoin([shareFindingEnabled$, hasIssueReporters$]).subscribe(([canShareFinding, hasIssueReporters]) => {
            this.buildMenu(canShareFinding, hasIssueReporters);
            this.cdr.markForCheck();
        });
    }

    private buildMenu(canShareFinding: boolean, hasIssueReporters: boolean): void {
        this.menuItems = [];

        if (canShareFinding) {
            this.menuItems.push({
                label: 'Create Report Sharing Link',
                command: () => {
                    this.shareFinding();
                }
            });
        }

        if (hasIssueReporters) {
            this.menuItems.push({
                label: 'Create Jira Issue',
                command: () => {
                    this.openIssueReporter();
                }
            });
        }

    }

    private openIssueReporter(): void {
        if (!this.projectId || !this.findingIds?.length) {
            return;
        }
        this.toolService.openTool(IssueReporterComponent, new IssueReporterToolConfig(this.projectId, IssueSourceType.Finding, this.findingIds[0]));
    }

    shareFinding() {
        if (!this.sharePopover || !this.projectId || !this.findingIds?.length) {
            console.warn("component is missing required inputs");
            return;
        }

        this.sharing = true;
        this.testService.createFindingReport(this.projectId, this.findingIds).pipe(
            take(1),
            mergeMap((reportId) => {
                return this.reportService.createNewSharingToken$(reportId, 'Finding Report')
            })
        ).subscribe({
            next: (sharingToken) => {
                this.shareLink = this.makeShareLinkFromToken(sharingToken.token);
                this.sharePopover.show(event, this.shareButton.el.nativeElement);
                this.cdr.markForCheck();
            }, error: (err) => {
                this.notificationService.toastErrorNotification('Failed to create share link');
                console.error(err);
            }, complete: () => {
                this.sharing = false;
            }
        });
    }

    copyToken(): void {
        if (!this.shareLink) {
            return;
        }

        copyToClipboard(this.shareLink).then(() => {
            this.shareLinkCopied = true;
            setTimeout(() => this.shareLinkCopied = false, 1500);
        });
    }

    private makeShareLinkFromToken(token: string): string {
        const link = this.router.createUrlTree(['report', 'shared', token]).toString()
        return window.location.origin + link;
    }

    protected readonly faShareAlt = faShareAlt;
    protected readonly faCopy = faCopy;
    protected readonly faCheck = faCheck;
    protected readonly faSyncAlt = faSyncAlt;
}
